import { useMemo } from "react";
import type { Channel, Program, SeriesItem, VodItem } from "@hybrid-iptv/types";
import type { ChannelProgramIndex } from "../epg/hooks.js";
import { bestAiring } from "./oneAiringPerShow.js";

/**
 * Universal search (Section 11 step 1) — one query across channels, EPG
 * programmes, movies, and series.
 *
 * Runs entirely client-side against catalogs already in memory/IndexedDB
 * (Sections 9–10 seed them), so typing costs no provider connections and no
 * API round-trip. That is a deliberate consequence of the connection cap: a
 * server-side search endpoint would have hit the provider on every keystroke.
 */

export type SearchKind = "channel" | "program" | "movie" | "series";

export interface SearchHit {
  kind: SearchKind;
  /** Stable identity for the underlying record. */
  id: string;
  title: string;
  /** Secondary line — channel name, year, air time. */
  subtitle?: string;
  posterUrl?: string;
  /** Higher sorts first. */
  score: number;
  channel?: Channel;
  program?: Program;
  movie?: VodItem;
  series?: SeriesItem;
}

export interface SearchResults {
  channels: SearchHit[];
  programs: SearchHit[];
  movies: SearchHit[];
  series: SearchHit[];
  total: number;
}

/** Per-type cap. A provider catalog is tens of thousands of rows and a
 * two-character query matches a huge slice of it; rendering all of them would
 * stall the TV's compositor for seconds. */
const PER_KIND_CAP = 40;

/** Below this, results are noise — "a" would match nearly everything. */
const MIN_QUERY = 2;

export function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // strip accents so "asu mare" finds "¡Asu Mare!"
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Rank a candidate against the query. Exact > prefix > word-boundary >
 * substring, so "espn" puts "ESPN" above "ESPN Deportes" above
 * "Local ESPN Affiliate" — the ordering a viewer expects when they type a
 * channel name they already know.
 */
export function scoreMatch(haystack: string, needle: string): number {
  if (!haystack) return 0;
  if (haystack === needle) return 100;
  if (haystack.startsWith(needle)) return 80 - Math.min(haystack.length - needle.length, 20) * 0.5;
  const wordBoundary = haystack.includes(` ${needle}`);
  if (wordBoundary) return 60;
  if (haystack.includes(needle)) return 40;
  return 0;
}

interface SearchInput {
  query: string;
  channels: Channel[];
  programIndex: ChannelProgramIndex | null;
  movies: VodItem[];
  series: SeriesItem[];
  /** Channel ids the user has hidden — hidden content stays out of search,
   * otherwise "hide" would only mean "hide from the guide". */
  hiddenChannelIds?: Set<string>;
  /** The viewer's home market (home/liveNowPicks.ts localMarketFrom), when their stations say it. */
  market?: string | null;
}

export function useUniversalSearch({
  query,
  channels,
  programIndex,
  movies,
  series,
  hiddenChannelIds,
  market = null,
}: SearchInput): SearchResults {
  return useMemo(() => {
    const needle = normalize(query);
    const empty: SearchResults = { channels: [], programs: [], movies: [], series: [], total: 0 };
    if (needle.length < MIN_QUERY) return empty;

    const take = (hits: SearchHit[]) =>
      hits.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title)).slice(0, PER_KIND_CAP);

    const channelHits: SearchHit[] = [];
    for (const channel of channels) {
      if (hiddenChannelIds?.has(channel.id)) continue;
      // Channel numbers are a first-class way to find a channel on a remote,
      // so an exact number match ranks at the top.
      const numberMatch = channel.number !== undefined && String(channel.number) === needle;
      const score = numberMatch ? 100 : scoreMatch(normalize(channel.name), needle);
      if (score > 0) {
        channelHits.push({
          kind: "channel",
          id: channel.id,
          title: channel.name,
          subtitle: channel.number ? `Channel ${channel.number}` : undefined,
          posterUrl: channel.logoUrl ?? undefined,
          score,
          channel,
        });
      }
    }

    // One row per show: a syndicated show is on dozens of local stations at once, and the viewer
    // wants the one that is theirs (search/oneAiringPerShow.ts).
    const programHits: SearchHit[] = [];
    if (programIndex) {
      const byChannel = new Map(channels.map((c) => [c.id, c]));
      const now = Date.now();
      const byTitle = new Map<string, Array<{ program: Program; channel: Channel; score: number }>>();
      for (const program of programIndex.allPrograms()) {
        const channel = byChannel.get(program.channelId);
        if (!channel || hiddenChannelIds?.has(channel.id)) continue;
        const title = normalize(program.title);
        const score = scoreMatch(title, needle);
        if (score <= 0) continue;
        const group = byTitle.get(title);
        if (group) group.push({ program, channel, score });
        else byTitle.set(title, [{ program, channel, score }]);
      }
      for (const group of byTitle.values()) {
        const pick = bestAiring(
          group.map(({ program, channel }) => ({
            channelName: channel.name,
            channelNumber: channel.number,
            start: Date.parse(program.start),
            end: Date.parse(program.end),
          })),
          now,
          market,
        );
        const chosen = pick >= 0 ? group[pick] : undefined;
        if (!chosen) continue;
        const onNow = Date.parse(chosen.program.start) <= now;
        programHits.push({
          kind: "program",
          id: chosen.program.id,
          title: chosen.program.title,
          subtitle: `${chosen.channel.name} · ${onNow ? "On now" : formatAirTime(chosen.program.start)}`,
          score: chosen.score,
          channel: chosen.channel,
          program: chosen.program,
        });
      }
    }

    const movieHits: SearchHit[] = [];
    for (const movie of movies) {
      const score = scoreMatch(normalize(movie.rawTitle), needle);
      if (score > 0) {
        movieHits.push({
          kind: "movie",
          id: movie.id,
          title: movie.rawTitle,
          subtitle: movie.year ? String(movie.year) : undefined,
          posterUrl: movie.posterUrl ?? undefined,
          score,
          movie,
        });
      }
    }

    const seriesHits: SearchHit[] = [];
    for (const item of series) {
      const score = scoreMatch(normalize(item.rawTitle), needle);
      if (score > 0) {
        // SeriesItem carries no year (see packages/types) — season count is the
        // only summary metadata available without a lazy details fetch, and
        // fetching per result would hit the provider on every keystroke.
        const seasonCount = item.seasons?.length ?? 0;
        seriesHits.push({
          kind: "series",
          id: item.id,
          title: item.rawTitle,
          subtitle: seasonCount > 0 ? `${seasonCount} season${seasonCount === 1 ? "" : "s"}` : undefined,
          posterUrl: item.posterUrl ?? undefined,
          score,
          series: item,
        });
      }
    }

    const out = {
      channels: take(channelHits),
      programs: take(programHits),
      movies: take(movieHits),
      series: take(seriesHits),
      total: 0,
    };
    out.total = out.channels.length + out.programs.length + out.movies.length + out.series.length;
    return out;
  }, [query, channels, programIndex, movies, series, hiddenChannelIds, market]);
}

function formatAirTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${m}${ampm}`;
}
